import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Stack, Typography } from "@mui/material";
import {
  paymentQueryKeys,
  readKnownDetachedPaymentMethodIds,
  readKnownUncertainPaymentMethodIds,
  removingPaymentMethodIdsQueryKey,
  useFrontendConfig,
  usePaymentMethods,
} from "@bw/shared/hooks";
import type { TournamentRegistrationPaymentResponse } from "@bw/shared/types/generated";
import {
  MaterialConnectedElementsProvider,
  MaterialPaymentMethodPicker,
} from "../../../common/components/payment";
import { RegistrationCardSetupChallenge } from "./RegistrationCardSetupChallenge";
import { cardSetupClientSecret } from "../utils/cardSetupAuthentication";
import { useRegistrationCardSetup } from "../hooks/useRegistrationCardSetup";

/** Mounted only after a payer explicitly opens this one recovery panel. */
export function GuardianCardSetupRecovery({
  payment,
}: {
  payment: TournamentRegistrationPaymentResponse;
}) {
  const client = useQueryClient();
  const methods = usePaymentMethods(payment.facility_id);
  const config = useFrontendConfig(payment.facility_id);
  const { resume, release } = useRegistrationCardSetup(
    payment.tournament_id,
    payment.registration_checkout_id,
  );
  const [cardId, setCardId] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [bankBusy, setBankBusy] = useState(false);
  const [walletUnavailable, setWalletUnavailable] = useState(false);
  const lock = useRef(false);
  const mounted = useRef(true);
  const secretRef = useRef(secret);
  secretRef.current = secret;
  const selectedRef = useRef(cardId);
  selectedRef.current = cardId;
  const usable = Boolean(
    cardId &&
      !methods.isError &&
      !methods.isFetching &&
      methods.data?.some(
        (method) =>
          method.payment_method_id === cardId && method.is_usable === true,
      ),
  );
  const usableRef = useRef(usable);
  usableRef.current = usable;
  const methodsRef = useRef(methods.data);
  methodsRef.current = methods.data;
  function usableNow() {
    const selected = selectedRef.current;
    const cards = methodsRef.current ?? [];
    const key = paymentQueryKeys.paymentMethods(payment.facility_id);
    return Boolean(
      selected &&
        usableRef.current &&
        !readKnownDetachedPaymentMethodIds(client, key, cards).includes(
          selected,
        ) &&
        !readKnownUncertainPaymentMethodIds(client, key, cards).includes(
          selected,
        ) &&
        !(
          client.getQueryData<string[]>(
            removingPaymentMethodIdsQueryKey(key, cards),
          ) ?? []
        ).includes(selected),
    );
  }
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const busy = resume.isPending || release.isPending || bankBusy;
  async function save() {
    if (
      lock.current ||
      busy ||
      !payment.can_resume_card_setup ||
      !usable ||
      !cardId
    )
      return;
    if (!usableNow()) {
      setWalletUnavailable(true);
      return;
    }
    lock.current = true;
    try {
      await resume.mutateAsync(cardId);
    } catch (error) {
      if (mounted.current) setSecret(cardSetupClientSecret(error));
    } finally {
      lock.current = false;
    }
  }
  async function authenticated(clientSecret: string) {
    if (
      !mounted.current ||
      clientSecret !== secretRef.current ||
      !selectedRef.current ||
      !usableNow()
    ) {
      if (mounted.current) setWalletUnavailable(true);
      throw new Error("Card setup context changed");
    }
    await resume.mutateAsync(selectedRef.current);
  }
  async function releaseAttempt() {
    if (lock.current || busy || !payment.can_release_card_setup) return;
    lock.current = true;
    try {
      await release.mutateAsync();
    } catch {
      /* Safe mutation error below. */
    } finally {
      lock.current = false;
    }
  }
  return (
    <Stack
      component="section"
      aria-label="Complete card setup"
      spacing={1.5}
      sx={{ width: "100%" }}
    >
      <Typography variant="body2">
        Use the original saved card. Saving it does not charge the entry fee or
        grant guardian approval.
      </Typography>
      {methods.isLoading || config.isLoading ? (
        <Typography role="status">Loading saved cards…</Typography>
      ) : methods.isError || config.isError ? (
        <Alert
          severity="error"
          action={
            <Button
              onClick={() => {
                void methods.refetch();
                void config.refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Could not load card verification options.
        </Alert>
      ) : !methods.data?.length ? (
        <Typography>
          No saved cards are available. Release this attempt to register again
          with another card.
        </Typography>
      ) : (
        <MaterialPaymentMethodPicker
          methods={methods.data}
          selectedId={cardId}
          onSelect={(value) => {
            setCardId(value);
            setWalletUnavailable(false);
          }}
          disabled={busy || Boolean(secret)}
          allowRemove={false}
          facilityId={payment.facility_id}
          paymentMethodsQueryKey={paymentQueryKeys.paymentMethods(
            payment.facility_id,
          )}
        />
      )}
      {!secret && payment.can_resume_card_setup ? (
        <Button
          sx={{ alignSelf: "flex-start" }}
          disabled={busy || !usable || !config.data || config.isError}
          onClick={() => void save()}
        >
          Save card
        </Button>
      ) : null}
      {secret && config.data ? (
        <MaterialConnectedElementsProvider config={config.data}>
          <RegistrationCardSetupChallenge
            key={secret}
            clientSecret={secret}
            onAuthenticated={authenticated}
            onProcessingChange={setBankBusy}
          />
        </MaterialConnectedElementsProvider>
      ) : null}
      {walletUnavailable ? (
        <Alert severity="error">
          The original card is no longer available. Refresh your cards or
          release this attempt.
        </Alert>
      ) : null}
      {resume.isError && !secret ? (
        <Alert severity="error">
          Could not finish card setup. Use the original card or release this
          attempt and register again.
        </Alert>
      ) : null}
      {payment.can_release_card_setup ? (
        <>
          <Typography variant="body2" color="text.secondary">
            Release this unfinished registration to start again. This does not
            remove your saved card or cancel an entry already awaiting guardian
            approval.
          </Typography>
          <Button
            sx={{ alignSelf: "flex-start" }}
            disabled={busy}
            onClick={() => void releaseAttempt()}
          >
            Release attempt
          </Button>
        </>
      ) : null}
      {release.isError ? (
        <Alert severity="error">
          Could not release this attempt. Check its latest status.
        </Alert>
      ) : null}
    </Stack>
  );
}
